import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CurrencyPipe } from '@angular/common';
import { AgChartsModule } from 'ag-charts-angular';
import { AgChartOptions } from 'ag-charts-community';
import { CollapseModule } from 'ngx-bootstrap/collapse';
import { take } from 'rxjs';
import { AccountingReportsService } from '../accounting-reports.service';
import { SharedService } from 'src/app/shared/services/shared.service';
import { CogsPdfGenerator } from './cogs-pdf';

@Component({
  selector: 'app-cost-of-goods-sold',
  standalone: true,
  imports: [FormsModule, CurrencyPipe, AgChartsModule, CollapseModule],
  templateUrl: './cost-of-goods-sold.component.html',
  styleUrl: './cost-of-goods-sold.component.css'
})
export class CostOfGoodsSoldComponent implements OnInit {
  public chartOptions!: AgChartOptions;
  public distributionOptions!: AgChartOptions;
  public comparisonOptions!: AgChartOptions;
  public period: string = 'This Year';
  public periods: string[] = ['This Month', 'Last Month', 'This Quarter', 'This Year', 'Custom'];
  public isChartCollapsed: boolean = false;
  public isBreakdownCollapsed: boolean = true;
  public startDate: string = '';
  public endDate: string = '';

  // Summary Metrics
  public beginningInventory: number = 0;
  public purchases: number = 0;
  public totalCogs: number = 0;
  public endingInventory: number = 0;
  public grossMargin: number = 0; // percentage

  // Mock data for table breakdown (by product category)
  public cogsBreakdown: any[] = [];
  public chartData: any[] = [];

  constructor(
    private reportsService: AccountingReportsService,
    private sharedService: SharedService
  ) {}

  exportPdf() {
    const generator = new CogsPdfGenerator();
    const companyInfo = this.sharedService.company_info;
    const data = {
      beginningInventory: this.beginningInventory,
      purchases: this.purchases,
      totalCogs: this.totalCogs,
      endingInventory: this.endingInventory,
      breakdown: this.cogsBreakdown
    };
    const doc = generator.generatePdf(data, companyInfo, this.period, this.startDate, this.endDate);
    doc.save(`COGS_Report_${this.period.replace(' ', '_')}.pdf`);
  }

  ngOnInit() {
    if (this.sharedService.company_info_loaded) {
      this.setPeriod('This Year');
      return;
    }

    this.sharedService.company_info_retrieved$.pipe(take(1)).subscribe(() => {
      this.setPeriod('This Year');
    });
  }

  loadData() {
    this.reportsService.getCogs(this.startDate, this.endDate).subscribe({
      next: (res) => {
        if (res.success && res.data) {
          const d = res.data;
          this.beginningInventory = d.beginningInventory || 0;
          this.purchases = d.purchases || 0;
          this.totalCogs = d.totalCogs || 0;
          this.endingInventory = d.endingInventory || 0;
          this.cogsBreakdown = d.breakdown || [];
          this.chartData = d.chartData || [];
          this.initChart();
        }
      },
      error: (err) => console.error("Error loading COGS data:", err)
    });
  }

  setPeriod(p: string) {
    this.period = p;
    if (p === 'Custom') return;

    const today = new Date();
    let start = new Date();
    let end = new Date();

    if (p === 'This Month') {
      start = new Date(today.getFullYear(), today.getMonth(), 1);
      end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    } else if (p === 'Last Month') {
      start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      end = new Date(today.getFullYear(), today.getMonth(), 0);
    } else if (p === 'This Quarter') {
      const quarter = Math.floor(today.getMonth() / 3);
      start = new Date(today.getFullYear(), quarter * 3, 1);
      end = new Date(today.getFullYear(), (quarter + 1) * 3, 0);
    } else if (p === 'This Year') {
      start = new Date(today.getFullYear(), 0, 1);
      end = new Date(today.getFullYear(), 11, 31);
    }

    this.startDate = this.formatDate(start);
    this.endDate = this.formatDate(end);
    this.loadData();
  }

  applyCustomRange() {
    if (this.startDate && this.endDate) {
      this.loadData();
    }
  }

  private formatDate(date: Date): string {
    const d = new Date(date);
    let month = '' + (d.getMonth() + 1);
    let day = '' + d.getDate();
    const year = d.getFullYear();

    if (month.length < 2) month = '0' + month;
    if (day.length < 2) day = '0' + day;

    return [year, month, day].join('-');
  }

  private initChart() {
    // 1. Trend Chart (Existing)
    this.chartOptions = {
      theme: 'ag-default-dark',
      background: { fill: 'transparent' },
      title: { text: 'Monthly Trend', color: 'white', fontSize: 13 },
      data: this.chartData,
      series: [
        {
          type: 'bar',
          xKey: 'month',
          yKey: 'cogs',
          yName: 'COGS',
          fill: '#ff4d4d',
          cornerRadius: 4,
        },
        {
          type: 'line',
          xKey: 'month',
          yKey: 'margin',
          yName: 'Margin %',
          stroke: '#00a8ff',
          strokeWidth: 3,
          marker: { fill: '#00a8ff', stroke: '#1e1e1e', strokeWidth: 2 }
        }
      ],
      axes: [
        { 
          type: 'category', 
          position: 'bottom', 
          label: { color: 'rgba(255, 255, 255, 0.7)' } 
        },
        { 
          type: 'number', 
          position: 'left', 
          keys: ['cogs'], 
          label: { 
            color: 'rgba(255, 255, 255, 0.7)',
            formatter: (params: any) => '$' + (params.value / 1000).toFixed(0) + 'k'
          }, 
          gridLine: { style: [{ stroke: 'rgba(255, 255, 255, 0.05)' }] } 
        },
        { 
          type: 'number', 
          position: 'right', 
          keys: ['margin'], 
          label: { 
            color: 'rgba(255, 255, 255, 0.7)',
            formatter: (params: any) => params.value + '%'
          } 
        }
      ],
      legend: { enabled: false }
    } as any;

    // 2. Distribution (Donut) - Not used in template but kept for logic consistency
    this.distributionOptions = {
      theme: 'ag-default-dark',
      background: { fill: 'transparent' },
      title: { text: 'COGS by Category', color: 'white', fontSize: 13 },
      data: this.cogsBreakdown,
      series: [
        {
          type: 'donut',
          angleKey: 'amount',
          calloutLabelKey: 'category',
          innerRadiusRatio: 0.7,
          strokeWidth: 0,
          sectorLabelKey: 'amount',
          sectorLabel: {
            color: 'white',
            fontWeight: 500,
            formatter: (params) => '$' + (params.value / 1000).toFixed(0) + 'k',
          },
          fills: ['#ff4d4d', '#ff9800', '#9c27b0', '#e91e63', '#673ab7'],
        },
      ],
      legend: { enabled: false },
    } as any;

    // 3. Comparison (Bar)
    this.comparisonOptions = {
      theme: 'ag-default-dark',
      background: { fill: 'transparent' },
      title: { text: 'Purchase vs COGS', color: 'white', fontSize: 13 },
      data: [
        { type: 'Purchases', amount: this.purchases },
        { type: 'COGS', amount: this.totalCogs }
      ],
      series: [
        {
          type: 'bar',
          xKey: 'type',
          yKey: 'amount',
          fills: ['#00a8ff', '#ff4d4d'],
          strokeWidth: 0,
          cornerRadius: 4,
          label: {
            enabled: true,
            color: 'white',
            fontWeight: 500,
            formatter: (params) => '$' + (params.value / 1000).toFixed(0) + 'k'
          }
        }
      ],
      axes: [
        { 
          type: 'category', 
          position: 'bottom', 
          label: { color: 'white' } 
        },
        { 
          type: 'number', 
          position: 'left', 
          label: { 
            color: 'rgba(255, 255, 255, 0.7)',
            formatter: (params: any) => '$' + (params.value / 1000).toFixed(0) + 'k'
          }, 
          gridLine: { style: [{ stroke: 'rgba(255, 255, 255, 0.05)' }] } 
        }
      ],
      legend: { enabled: false }
    } as any;
  }
}
